import React, { useState, useMemo } from "react";
import { useRoleVerify } from "Common/RoleVerify";
import { GetAll, Delete } from "adminApi/PurchaseItemApi/PurchaseItem";
import DataTable from "Common/DataTable/DataTable";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import usePaginatedFetch from "Hooks/usePaginatedFetch";
import useTableConfig from "Hooks/useTableConfig";
import { generateDisplayFields } from "utils/fieldMapper";
import fields from "../../json/PurchaseItemFeilds.json";

const PurchaseItemListTable = () => {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search, 500);
  const canShowAction = useRoleVerify("", ["can_edit", "can_delete"]);
  const sizeOptions = [10, 20, 50, 100];
  const { tabledata } = fields;

  // Fetch Hook
  const { data, meta, loading, error, refetch } = usePaginatedFetch(GetAll, {
    page,
    limit,
    search: debouncedSearch,
    title: "Purchase Item",
  });

  const displayFields = generateDisplayFields(tabledata);

  // Memoized: format date and status badge — guarded against null purchase_date
  const formattedData = useMemo(() => {
    return data.map((item) => {
      // Guard against null/undefined purchase_date
      let formattedDate = item.purchase_date || "-";
      if (item.purchase_date) {
        const parts = item.purchase_date.split("T")[0]?.split("-");
        if (parts?.length === 3) {
          const [year, month, day] = parts;
          formattedDate = `${day}/${month}/${year}`;
        }
      }

      return {
        ...item,
        purchase_date: formattedDate,
        status:
          item.status == "1" ? (
            <span className="badge bg-success">Paid</span>
          ) : (
            <span className="badge bg-danger">Unpaid</span>
          ),
      };
    });
  }, [data]);

  const handleSearch = (val, resetPage = null) => {
    setSearch(val);
    if (resetPage !== null) setPage(resetPage);
  };

  const onPageChange = (p) => setPage(p);
  const onLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  // Table Hook
  const { formattedData: tableRows, columns } = useTableConfig({
    data: formattedData,
    page,
    limit,
    canShowAction,
    refetch,
    IsDelete: true,
    config: {
      entityName: "Purchase Item",
      editUrl: "edit-purchase-item",
      idKey: "purchase_id",
      deleteApi: Delete,
      displayFields,
    },
  });

  return (
    <DataTable
      columns={columns}
      data={tableRows}
      options={{
        loading,
        error,
        sortableColumns: displayFields.map((f) => f.accessor),
        copy: true,
        csv: true,
        pdf: true,
        print: true,
      }}
      Search={{
        search: true,
        SearchInputValue: search,
        handleSearch: handleSearch,
      }}
      Entries={{
        Entries: true,
        limit,
        onLimitChange,
        options: sizeOptions,
      }}
      pagination={{
        isActive: true,
        meta,
        onPageChange,
      }}
    />
  );
};

export default PurchaseItemListTable;
